import React, { useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Button,
    Stack,
    Card,
    CardContent,
    CardActions,
    Box,
    Divider,
    Chip,
    useTheme,
    useMediaQuery,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuth } from '../context/AuthContext';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ContactList = ({
    contacts,
    branches = [],
    selectedBusinessId,
    onUpdateClick,
    onDeleteClick,
    onGrantAccessClick,
    onRevokeAccessClick = () => {},
    grantingContactId = '',
}) => {
    const { isAdmin, userRole, userContactId } = useAuth();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const branchesByContact = useMemo(() => {
        const map = new Map();
        branches.forEach(branch => {
            (branch.supervisorIds || []).forEach(id => {
                if (!map.has(id)) {
                    map.set(id, []);
                }
                map.get(id).push(branch);
            });
        });
        return map;
    }, [branches]);

    const shouldShowAssociatedBranches = (contact) => contact.role === 'Supervisor' || contact.role === 'Employee';

    const resolveAssociatedBranches = (contact) => {
        if (!branches.length) return [];
        if (!shouldShowAssociatedBranches(contact)) return [];
        const branchList = branchesByContact.get(contact.contactId) || [];
        return branchList.map(branch => branch.nickname || branch.address);
    };

    if (!contacts || contacts.length === 0) {
        return <Typography variant="body1">No contacts found for the selected business.</Typography>;
    }

    const canModifyContact = (contact) => {
        if (isAdmin) return true;
        if (userRole === 'Guest') return false;
        if (userRole === 'Supervisor') {
            if (contact.role === 'Business Owner') return false;
            return contact.contactId === userContactId;
        }
        return true;
    };

    const canDeleteContact = (contact) => {
        if (!canModifyContact(contact)) return false;
        if (userRole === 'Supervisor' && contact.contactId === userContactId) {
            return false;
        }
        return true;
    };

    const renderActions = (contact, justify = 'flex-end') => {
        const iconButtonSx = {
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
                bgcolor: theme.palette.action.hover,
            },
        };
        const isEditable = canModifyContact(contact);
        const isDeletable = canDeleteContact(contact);
        const deleteTooltip = isDeletable
            ? 'Delete contact'
            : userRole === 'Supervisor' && contact.contactId === userContactId
                ? 'Supervisors cannot delete their own profile'
                : 'You do not have permission to delete';

        return (
            <Stack direction="row" spacing={1} justifyContent={justify}>
                <Tooltip title={isEditable ? 'Edit contact' : 'You do not have permission to edit'}>
                    <span>
                        <IconButton
                            size="small"
                            sx={iconButtonSx}
                            onClick={() => onUpdateClick(contact)}
                            disabled={!isEditable}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={deleteTooltip}>
                    <span>
                        <IconButton
                            size="small"
                            color="error"
                            sx={iconButtonSx}
                            onClick={() => onDeleteClick(contact)}
                            disabled={!isDeletable}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        );
    };

    const renderAssociated = (contact, justify = 'flex-start') => {
        if (!shouldShowAssociatedBranches(contact)) {
            return <Typography variant="body2" color="text.secondary"></Typography>;
        }
        const associated = resolveAssociatedBranches(contact);
        if (!associated.length) {
            return <Typography variant="body2" color="text.secondary">None</Typography>;
        }
        return (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} justifyContent={justify}>
                {associated.map(label => (
                    <Chip key={label} label={label} size="small" />
                ))}
            </Stack>
        );
    };

    const contactBusinessId = (contact) => {
        if (typeof contact.PK === 'string' && contact.PK.startsWith('BUSINESS#')) {
            return contact.PK.replace('BUSINESS#', '');
        }
        return contact.businessId || null;
    };

    const isSameBusiness = (contact) => {
        if (isAdmin) return true;
        const bizId = contactBusinessId(contact);
        if (!selectedBusinessId) return true;
        return bizId === selectedBusinessId;
    };

    const canGrantAccess = (contact) => {
        if (contact.firebaseUid) return false;
        if (!isSameBusiness(contact)) return false;
        if (isAdmin) return true;
        const targetRole = contact.role;
        if (userRole === 'Business Owner') {
            return ['Supervisor', 'Employee', 'Guest'].includes(targetRole);
        }
        if (userRole === 'Supervisor') {
            return ['Employee', 'Guest'].includes(targetRole);
        }
        return false;
    };

    const canRevokeAccess = (contact) => {
        if (!contact.firebaseUid) return false;
        if (!isSameBusiness(contact)) return false;
        if (isAdmin) return true;
        const targetRole = contact.role;
        if (userRole === 'Business Owner') {
            return ['Supervisor', 'Employee'].includes(targetRole);
        }
        if (userRole === 'Supervisor') {
            return ['Employee', 'Guest'].includes(targetRole);
        }
        return false;
    };

    const renderAppAccess = (contact, justify = 'flex-start') => {
        const actions = [];
        const statusChip = contact.firebaseUid ? (
            <Chip label="Access Granted" size="small" color="success" />
        ) : (
            <Chip label="No Access" size="small" />
        );

        if (!contact.firebaseUid && canGrantAccess(contact)) {
            const isGranting = grantingContactId === contact.contactId;
            actions.push(
                <Button
                    key={`grant-${contact.contactId}`}
                    variant="contained"
                    size="small"
                    onClick={() => onGrantAccessClick(contact)}
                    disabled={isGranting}
                    startIcon={isGranting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isGranting ? 'Sending…' : 'Grant Access'}
                </Button>
            );
        }

        if (contact.firebaseUid && canRevokeAccess(contact)) {
            actions.push(
                <Button
                    key={`revoke-${contact.contactId}`}
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => onRevokeAccessClick(contact)}
                    disabled={grantingContactId === contact.contactId}
                >
                    Revoke
                </Button>
            );
        }

        return (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent={justify}>
                {statusChip}
                {actions.length > 0 && (
                    <Stack direction="row" spacing={1}>
                        {actions}
                    </Stack>
                )}
            </Stack>
        );
    };

    if (!isDesktop) {
        return (
            <Stack spacing={2}>
                {contacts.map(contact => (
                    <Card key={contact.contactId} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                <BadgeIcon color="primary" />
                                <Box>
                                    <Typography variant="h6">{contact.contactName}</Typography>
                                    <Chip label={contact.role} size="small" color="secondary" sx={{ mt: 0.5 }} />
                                </Box>
                            </Stack>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack spacing={1.25}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PhoneIcon color="action" fontSize="small" />
                                    <Typography variant="body2">{contact.phoneNumber}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <EmailIcon color="action" fontSize="small" />
                                    <Typography variant="body2">{contact.email || 'N/A'}</Typography>
                                </Stack>
                                {shouldShowAssociatedBranches(contact) ? (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Associated Branches</Typography>
                                        {renderAssociated(contact, 'flex-start')}
                                    </Box>
                                ) : null}
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Access</Typography>
                                    {renderAppAccess(contact, 'flex-start')}
                                </Box>
                            </Stack>
                        </CardContent>
                        <CardActions sx={{ px: 2, pb: 2 }}>
                            {renderActions(contact, 'flex-start')}
                        </CardActions>
                    </Card>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Contact Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Associated Branches</TableCell>
                        <TableCell>App Access</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {contacts.map((contact) => (
                        <TableRow key={contact.contactId}>
                            <TableCell>{contact.contactName}</TableCell>
                            <TableCell>{contact.role}</TableCell>
                            <TableCell>{contact.phoneNumber}</TableCell>
                            <TableCell>{contact.email || 'N/A'}</TableCell>
                            <TableCell>{renderAssociated(contact)}</TableCell>
                            <TableCell>{renderAppAccess(contact)}</TableCell>
                            <TableCell align="right">
                                {renderActions(contact)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ContactList;
